"use client";

import { Button } from "@cgc/ui/button";
import { Leaf2 } from "@cgc/ui/icons";
import { Heading } from "@cgc/ui/typography";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import type { JSX } from "react";
import { Fragment, useCallback, useState } from "react";

type ModalConfig = {
  title: string;
  body: () => JSX.Element;
  buttonLabel: string;
  destination: string;
};

type ModalProps = {
  config: ModalConfig;
  isOpen?: boolean;
  setIsOpen: (status: boolean) => void;
};

function SuccessModal({ config, isOpen = false }: ModalProps): JSX.Element {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          router.push(config.destination);
          setIsNavigating(true);
        }}
        open={isOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="text-cgc-blue relative w-full max-w-xs transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all lg:max-w-lg">
                <Leaf2
                  className="fill-cgc-green/30 absolute -left-6 -top-20 w-32 -rotate-90"
                  color={undefined}
                />

                <Button
                  className="absolute right-2 top-2 z-10 p-2"
                  color="unstyled"
                  onClick={() => {
                    router.push(config.destination);
                    setIsNavigating(true);
                  }}
                >
                  <XMarkIcon className="h-10 w-10" />
                </Button>

                <div className="relative z-0 space-y-4">
                  <Dialog.Title>
                    <div className="mr-8">
                      <Heading.h3>{config.title}</Heading.h3>
                    </div>
                  </Dialog.Title>

                  <Dialog.Description>{config.body()}</Dialog.Description>

                  <Button
                    className="w-full"
                    color="primary"
                    isLoading={isNavigating}
                    onClick={() => {
                      router.push(config.destination);
                      setIsNavigating(true);
                    }}
                  >
                    {config.buttonLabel}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export interface UseSuccessModalReturnType {
  Modal: React.FC<Omit<ModalProps, "setIsOpen">>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useSuccessModal = (): UseSuccessModalReturnType => {
  const [isOpen, setIsOpen] = useState(false);

  const MemoizedModal = useCallback(
    (props: Omit<ModalProps, "setIsOpen">) => (
      <SuccessModal {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
    ),
    [isOpen, setIsOpen],
  );

  return {
    setIsOpen,
    Modal: MemoizedModal,
  };
};
